import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, PromptSkill, ScriptSkill, VoicePreset, User, AnalysisMode } from '../types';
import { Language, LanguageSkill } from '../types/language';
import { listProjects, createProject, cloneProject, listSkillsForProject, listScriptSkillsForProject, listVoicePresetsForProject, listFavoriteProjects, favoriteProject, unfavoriteProject, listLanguages, listLanguageSkills, getExternalAnalysisAvailability, getRefTranslationLanguages } from '../api';
import HelpTip from '../components/HelpTip';
import SkillVariableForm from '../components/SkillVariableForm';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import CloneOptionsModal from './project_clone/CloneOptionsModal';
import { extractInheritedSettings, shouldLockSkillsAndLanguage, analysisModeTitleKey, buildCloneScriptRewriteFields, CloneInheritedSettings } from './project_clone/cloneInherit';
import { ScrollingProjectName } from '../components/ScrollingProjectName';
import { buildCreateScriptRewriteFields, validateCreateScriptRewrite } from './createProjectScriptRewrite';
import { pickDefaultShotSkill, shotSkillVariableDefaults } from './createProjectShotSkill';
import { useIsMobile } from '../utils/useIsMobile';
import { useDelayedLoading } from '../hooks/useDelayedLoading';

const PAGE_SIZE = 20;

/** 骨架卡片：布局近似真实项目卡（标题行/状态位/两行文本/底部 meta），只在初始加载时展示 */
function ProjectCardSkeleton() {
  return (
    <div className="card" aria-hidden="true" style={{ minHeight: 172 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <div className="skeleton" style={{ height: '1rem', width: '55%' }} />
        <div className="skeleton" style={{ height: '1rem', width: 48 }} />
      </div>
      <div className="skeleton" style={{ height: '0.8rem', width: '80%', marginTop: '0.9rem' }} />
      <div className="skeleton" style={{ height: '0.8rem', width: '65%', marginTop: '0.5rem' }} />
      <div className="skeleton" style={{ height: '0.8rem', width: '40%', marginTop: '1.4rem' }} />
    </div>
  );
}

const defaultFormState = () => ({
  name: '',
  projectPrompt: '',
  aspectRatio: '9:16' as string,
  skillId: '',
  skillVariableValues: {} as Record<string, string>,
  autoGenerateShots: false,
  analysisMode: 'external' as AnalysisMode,
  autoStartAnalysis: false,
  jimengModel: 'jimeng-5.0',
  charFixedPrompt: '参考图片来生成图片',
  sceneFixedPrompt: '参考图片来生成图片',
  propFixedPrompt: '参考图片来生成图片',
  enableVoiceInsertion: false,
  voicePrompt: '',
  disableBGM: true,
  scriptSkillId: '',
  scriptSkillValues: {} as Record<string, string>,
  projectType: 'animation' as string,
  outputLanguage: '',
  languageSkillId: '',
  languageSkillValues: {} as Record<string, string>,
  refLanguages: [] as string[],
});

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('projectList');
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState<Project[]>([]);
  const [skills, setSkills] = useState<PromptSkill[]>([]);
  const [scriptSkills, setScriptSkills] = useState<ScriptSkill[]>([]);
  const [voicePresets, setVoicePresets] = useState<VoicePreset[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [langSkills, setLangSkills] = useState<LanguageSkill[]>([]);
  const [langSkillsLoading, setLangSkillsLoading] = useState(false);
  const [refLangOptions, setRefLangOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showRewriteConfirm, setShowRewriteConfirm] = useState(false);
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  // 参考翻译引导弹窗（右下角「担心看不懂外语剧本？」文字链点开；只有在里面勾了语言才自动翻译）
  const [refLangGuideOpen, setRefLangGuideOpen] = useState(false);
  const [form, setForm] = useState(defaultFormState());
  // 创建表单的字段级校验提示（替代原生 alert，用站内内联红字）
  const [shotSkillError, setShotSkillError] = useState<string | null>(null);
  const [scriptSkillError, setScriptSkillError] = useState<string | null>(null);
  // 资产分析模式：默认全部折叠（连资深都不显示），但默认仍选中资深(external)。
  // 点击小标题左侧箭头展开全部 3 个选项；展开后若仍选资深，编辑其它字段时自动收起。
  const [showAnalysisOptions, setShowAnalysisOptions] = useState(false);
  // 资深资产分析是否在管理端启用（fail-closed：默认/异常都视为不可用）
  const [externalAvailable, setExternalAvailable] = useState(false);
  const [externalAvailabilityChecked, setExternalAvailabilityChecked] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // 用 ref 追踪当前页，避免闭包陈旧问题
  const pageRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const [activeTab, setActiveTab] = useState<'favorites' | 'all'>('favorites');
  const [cloneSourceId, setCloneSourceId] = useState<string | null>(null);
  const [cloneSourceName, setCloneSourceName] = useState<string>('');
  const [cloneSourceWorkflow, setCloneSourceWorkflow] = useState<Project['characterImageWorkflow']>();
  const [cloneStep0Open, setCloneStep0Open] = useState(false);
  const [cloneOptions, setCloneOptions] = useState<{ keepAssetImages: boolean; keepAssetImagePrompts: boolean; keepShotPrompts: boolean } | null>(null);
  const [cloneSourceInherited, setCloneSourceInherited] = useState<CloneInheritedSettings | null>(null);
  const activeTabRef = useRef<'favorites' | 'all'>('favorites');
  const loadGenRef = useRef(0); // generation counter to discard stale loads
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef('');
  const togglingRef = useRef<Set<string>>(new Set()); // in-flight favorite toggles

  const handleOpenCreate = () => {
    let currentUser: User | null = null;
    try { currentUser = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* corrupted */ }
    if (!currentUser?.phone) {
      setShowPhoneWarning(true);
      return;
    }
    setTypeSelectOpen(true);
  };

  useEffect(() => {
    loadProjects(0, true);
    loadSkills();
    loadScriptSkillsData();
    loadVoicePresetsData();
    listLanguages().then(data => setLanguages(data || [])).catch(() => { /* non-fatal */ });
  }, []);

  const handleSearch = () => setDebouncedSearch(searchInput);

  // 搜索词变化时重新加载
  useEffect(() => {
    searchRef.current = debouncedSearch;
    setProjects([]);
    setLoading(true);
    pageRef.current = 0;
    loadProjects(0, true);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLangSkillsLoading(true);
    listLanguageSkills({ status: 'published' })
      .then(data => {
        if (cancelled) return;
        const list = data || [];
        setLangSkills(list);
        const defaultLang = list.find(s => s.isDefault) || list[0];
        if (defaultLang) {
          const defaults: Record<string, string> = {};
          (defaultLang.variables || []).forEach(v => { if (v.default) defaults[v.key] = v.default; });
          setForm(prev => prev.languageSkillId ? prev : {
            ...prev,
            languageSkillId: defaultLang.id,
            outputLanguage: defaultLang.languageCode || '',
            languageSkillValues: defaults,
          });
        }
      })
      .catch(() => { if (!cancelled) setLangSkills([]); })
      .finally(() => { if (!cancelled) setLangSkillsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    getRefTranslationLanguages().then(r => setRefLangOptions(r.languages || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    getExternalAnalysisAvailability()
      .then((r) => { if (!cancelled) setExternalAvailable(r.available); })
      .catch(() => { if (!cancelled) setExternalAvailable(false); }) // fail-closed
      .finally(() => { if (!cancelled) setExternalAvailabilityChecked(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!externalAvailabilityChecked || externalAvailable) return;
    setForm(prev => prev.analysisMode === 'external' ? { ...prev, analysisMode: 'advanced' } : prev);
  }, [externalAvailabilityChecked, externalAvailable]);

  // 用户编辑「分析模式」以外的任意字段时，自动收起展开的高级/简易分析。
  // （切换 analysisMode 本身不触发——它被排除在签名外；选了高级/简易则靠下方 legacyVisible 持续可见。）
  const analysisOtherFieldsSig = JSON.stringify({ ...form, analysisMode: undefined });
  useEffect(() => { setShowAnalysisOptions(false); }, [analysisOtherFieldsSig]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    const gen = loadGenRef.current;
    try {
      const fetcher = activeTabRef.current === 'favorites' ? listFavoriteProjects : listProjects;
      const res = await fetcher(nextPage, searchRef.current);
      if (gen !== loadGenRef.current) return; // tab switched, discard
      const data = res.projects || [];
      if (nextPage * PAGE_SIZE + data.length >= res.total) {
        hasMoreRef.current = false;
        setHasMore(false);
      }
      if (data.length > 0) {
        pageRef.current = nextPage;
        setProjects(prev => [...prev, ...data]);
      }
    } catch (err) {
      console.error('Failed to load more projects:', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, loading]);

  // 分镜技能必填，且不让用户被迫手动选一次：技能加载后若 skillId 为空
  //（含关闭弹窗重置后重开、首屏慢加载等情况），自动兜底成系统默认分镜技能。
  // 克隆「保留分镜提示词」时字段锁定继承源（skillId 来自源项目），跳过兜底。
  useEffect(() => {
    const locked = shouldLockSkillsAndLanguage(!!cloneSourceId, cloneOptions?.keepShotPrompts === true);
    if (locked || skills.length === 0 || form.skillId) return;
    const def = pickDefaultShotSkill(skills);
    if (def) {
      setForm(prev => prev.skillId ? prev : { ...prev, skillId: def.id, skillVariableValues: shotSkillVariableDefaults(def) });
    }
  }, [skills, form.skillId, cloneSourceId, cloneOptions]);

  const loadProjects = async (p: number, reset = false) => {
    const gen = reset ? ++loadGenRef.current : loadGenRef.current;
    try {
      if (reset) {
        hasMoreRef.current = true;
        setHasMore(true);
      }
      const fetcher = activeTabRef.current === 'favorites' ? listFavoriteProjects : listProjects;
      const res = await fetcher(p, searchRef.current);
      if (gen !== loadGenRef.current) return; // tab switched, discard
      const data = res.projects || [];
      if (p * PAGE_SIZE + data.length >= res.total) {
        hasMoreRef.current = false;
        setHasMore(false);
      }
      pageRef.current = p;
      setProjects(reset ? data : prev => [...prev, ...data]);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  };

  const loadSkills = async () => {
    try {
      const data = await listSkillsForProject();
      const skillList = data || [];
      setSkills(skillList);
      const defaultSkill = pickDefaultShotSkill(skillList);
      if (defaultSkill) {
        setForm(prev => prev.skillId ? prev : { ...prev, skillId: defaultSkill.id, skillVariableValues: shotSkillVariableDefaults(defaultSkill) });
      }
    } catch (err) {
      console.error('Failed to load skills:', err);
    }
  };

  const loadScriptSkillsData = async () => {
    try {
      const data = await listScriptSkillsForProject();
      const list = data || [];
      setScriptSkills(list);
      const defaultScript = list.find(s => s.isDefault) || list[0];
      if (defaultScript) {
        const defaults: Record<string, string> = {};
        (defaultScript.variables || []).forEach(v => { if (v.default) defaults[v.key] = v.default; });
        setForm(prev => prev.scriptSkillId ? prev : { ...prev, scriptSkillId: defaultScript.id, scriptSkillValues: defaults });
      }
    } catch (err) {
      console.error('Failed to load script skills:', err);
    }
  };

  const loadVoicePresetsData = async () => {
    try {
      const data = await listVoicePresetsForProject();
      setVoicePresets(data || []);
    } catch (err) {
      console.error('Failed to load voice presets:', err);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setShotSkillError(null);
    setScriptSkillError(null);
    if (!form.name.trim()) return;
    if (!form.skillId) {
      setShotSkillError(t('alert.selectShotSkill'));
      return;
    }
    // 克隆+keepShotPrompts：剧本/分镜/语言技能恒继承源，跳过 create-only 的剧本改写校验/确认
    if (cloneSourceId && cloneOptions?.keepShotPrompts) {
      doCreateProject();
      return;
    }
    const scriptRewriteError = validateCreateScriptRewrite(
      form.scriptSkillId,
      scriptSkills,
      form.scriptSkillValues,
      t,
    );
    if (scriptRewriteError) {
      setScriptSkillError(scriptRewriteError);
      return;
    }

    if (form.scriptSkillId) {
      setShowRewriteConfirm(true);
      return;
    }

    doCreateProject();
  };

  const doCreateProject = async () => {
    setShowRewriteConfirm(false);
    setCreating(true);
    try {
      let project: Project;
      if (cloneSourceId && cloneOptions) {
        // Clone 流程：调 cloneProject，透传表单选中的技能和类型 + Step 0 toggles。
        // 剧本改写技能与分镜/语言技能一样在克隆向导里可见可编辑，故未勾 keepShotPrompts 时
        // 必须把 enableScriptNormalization + scriptSkillValues 一并发送（见
        // buildCloneScriptRewriteFields），否则会出现「scriptSkillId 被覆盖但开关仍继承源」的半状态。
        // keepShotPrompts 时这些恒继承源 → 不发送，后端浅拷贝保留源值。
        // languageSkillValues 仍不发送（语言技能变量沿用既有行为，不在本次改动范围）。
        project = await cloneProject(cloneSourceId, {
          name: form.name.trim(),
          projectType: form.projectType as 'real_person' | 'animation',
          skillId: form.skillId || undefined,
          scriptSkillId: form.scriptSkillId || undefined,
          languageSkillId: form.languageSkillId || undefined,
          outputLanguage: form.outputLanguage || undefined,
          disableBGM: form.disableBGM,
          keepAssetImages: cloneOptions.keepAssetImages,
          keepAssetImagePrompts: cloneOptions.keepAssetImagePrompts,
          keepShotPrompts: cloneOptions.keepShotPrompts,
          ...buildCloneScriptRewriteFields(cloneOptions.keepShotPrompts, form.scriptSkillId, form.scriptSkillValues),
        });
      } else {
        // 普通新建流程（保持原逻辑）
        project = await createProject({
          name: form.name.trim(),
          projectPrompt: form.projectPrompt.trim() || undefined,
          aspectRatio: form.aspectRatio,
          skillId: form.skillId || undefined,
          skillVariableValues: form.skillVariableValues,
          autoGenerateShots: form.autoGenerateShots,
          analysisMode: externalAvailable || form.analysisMode !== 'external' ? form.analysisMode : 'advanced',
          autoStartAnalysis: form.autoStartAnalysis,
          jimengModel: form.jimengModel,
          charFixedPrompt: form.charFixedPrompt,
          sceneFixedPrompt: form.sceneFixedPrompt,
          propFixedPrompt: form.propFixedPrompt,
          enableVoiceInsertion: form.enableVoiceInsertion,
          voicePrompt: form.enableVoiceInsertion ? form.voicePrompt.trim() : undefined,
          disableBGM: form.disableBGM,
          ...buildCreateScriptRewriteFields(form.scriptSkillId, form.scriptSkillValues),
          projectType: form.projectType,
          outputLanguage: form.outputLanguage || undefined,
          languageSkillId: form.languageSkillId || undefined,
          languageSkillValues: form.languageSkillId ? form.languageSkillValues : undefined,
          refLanguages: form.refLanguages.length > 0 ? form.refLanguages : undefined,
        });
      }
      setProjects([project, ...projects]);
      handleCloseModal();
      // 清理 clone 向导状态
      setCloneSourceId(null);
      setCloneSourceInherited(null);
      setCloneOptions(null);
      setCloneSourceName('');
      setCloneSourceWorkflow(undefined);
      // Phase 3/4：建完直接进入新项目的「剧本信息」页（主入口），由其硬强制流统一驱动
      // Step3 风格 → Step4 剧本（§3-② 同一模型覆盖创建向导，clone 自动正确）。
      navigate(`/projects/${project.id}/script-summary`);
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    if (creating) return;
    setTypeSelectOpen(false);
    setModalOpen(false);
    setRefLangGuideOpen(false);
    setForm(defaultFormState());
    setShotSkillError(null);
    setScriptSkillError(null);
    setCloneSourceId(null);
    setCloneSourceInherited(null);
    setCloneOptions(null);
    setCloneSourceName('');
    setCloneSourceWorkflow(undefined);
  };

  const handleTabChange = (tab: 'favorites' | 'all') => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    activeTabRef.current = tab;
    setProjects([]);
    setLoading(true);
    pageRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    loadProjects(0, true);
  };

  const handleToggleFavorite = async (e: React.MouseEvent, projectId: string, currentlyFavorited: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    if (togglingRef.current.has(projectId)) return; // prevent double-click
    togglingRef.current.add(projectId);
    // Optimistic update
    setProjects(prev => {
      if (activeTabRef.current === 'favorites' && currentlyFavorited) {
        return prev.filter(p => p.id !== projectId);
      }
      return prev.map(p => p.id === projectId ? { ...p, isFavorited: !currentlyFavorited } : p);
    });
    try {
      if (currentlyFavorited) {
        await unfavoriteProject(projectId);
      } else {
        await favoriteProject(projectId);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      // Rollback
      if (activeTabRef.current === 'favorites' && currentlyFavorited) {
        setLoading(true);
        loadProjects(0, true);
      } else {
        setProjects(prev =>
          prev.map(p => p.id === projectId ? { ...p, isFavorited: currentlyFavorited } : p)
        );
      }
    } finally {
      togglingRef.current.delete(projectId);
    }
  };

  // 150ms 内完成则整页留白（比闪一下骨架屏更稳）；超时后展示与真实卡片同布局的骨架网格
  // showSkeleton 也纳入守护：骨架屏一旦出现，minDuration 内即使 loading 已翻 false 也继续停留，防闪烁
  if (loading || showSkeleton) {
    return (
      <div className="container">
        {showSkeleton && (
          <div className="grid grid-2" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <ProjectCardSkeleton key={i} />
            ))}
          </div>
        )}
      </div>
    );
  }

  const isClone = !!cloneSourceId;
  const lockAnalysisMode = isClone;
  const lockSkillsAndLanguage = shouldLockSkillsAndLanguage(isClone, cloneOptions?.keepShotPrompts === true);
  const inheritedHint = (
    <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-3)' }}>
      {t('form.cloneInheritedHint')}
    </p>
  );

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">{t('title')}</h1>
        <button className="btn btn-primary" onClick={handleOpenCreate}>
          + {t('createProject')}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
        {([
          { key: 'favorites' as const, label: t('tabs.favorites') },
          { key: 'all' as const, label: t('tabs.all') },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            style={{
              padding: '0.6rem 1.2rem',
              fontSize: '0.9rem',
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-2)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={t('search.placeholder')}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              style={{
                padding: '5px 28px 5px 10px',
                fontSize: '0.82rem',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                outline: 'none',
                width: isMobile ? '120px' : '180px',
                background: 'var(--surface)',
                color: 'var(--text-1)',
              }}
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setDebouncedSearch(''); }}
                style={{
                  position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-3)', fontSize: '0.85rem', padding: '0 2px',
                }}
              >✕</button>
            )}
          </div>
          <button
            onClick={handleSearch}
            style={{
              padding: '5px 12px',
              fontSize: '0.82rem',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              background: 'var(--primary)',
              color: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >{t('search.button')}</button>
        </div>
      </div>

      {projects.length > 0 ? (
        <>
          <div className="grid grid-2 grid-fade-in">
            {projects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}/script-summary`}
                style={{ textDecoration: 'none' }}
              >
                <div
                  className="card interactive-card"
                  style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)', lineHeight: 1.35, display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, flex: 1 }}>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 500, padding: '1px 6px', borderRadius: '4px', flexShrink: 0, lineHeight: 1.6,
                        background: (project.projectType || 'animation') === 'real_person' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                        color: (project.projectType || 'animation') === 'real_person' ? 'var(--danger)' : 'var(--primary)',
                      }}>
                        {(project.projectType || 'animation') === 'real_person' ? t('card.realPerson') : t('card.animation')}
                      </span>
                      <ScrollingProjectName name={project.name} className="project-list-card-name" />
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                      <button
                        onClick={(e) => handleToggleFavorite(e, project.id, !!project.isFavorited)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          padding: '2px',
                          lineHeight: 1,
                          color: project.isFavorited ? '#f59e0b' : 'var(--text-3)',
                          transition: 'color 0.15s',
                        }}
                        title={project.isFavorited ? t('card.unfavorite') : t('card.favorite')}
                      >
                        {project.isFavorited ? '★' : '☆'}
                      </button>
                      {project.script && (
                        <span>
                          <StatusBadge status={project.script.status || 'draft'} />
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={{
                    margin: 0,
                    color: 'var(--text-2)',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1,
                  }}>
                    {project.projectPrompt || ' '}
                  </p>
                  {(project.assetCount != null || (project.episodeTotal != null && project.episodeTotal > 0)) && (
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {project.assetCount != null && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>
                          {t('card.assets')}<strong style={{ color: 'var(--text)' }}>{project.assetCount}</strong>
                        </span>
                      )}
                      {project.episodeTotal != null && project.episodeTotal > 0 && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>
                          {t('card.episodes')}<strong style={{ color: 'var(--text)' }}>{project.episodeCompleted}/{project.episodeTotal}</strong>
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>
                      {(() => { const d = new Date(project.createdAt); const pad = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; })()}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCloneSourceId(project.id);
                          setCloneSourceInherited(extractInheritedSettings(project));
                          setCloneSourceName(project.name);
                          setCloneSourceWorkflow(project.characterImageWorkflow);
                          setCloneStep0Open(true);
                        }}
                        title={t('card.cloneTooltip')}
                      >
                        {t('card.clone')}
                      </button>
                      {project.aspectRatio && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '1px 6px' }}>
                          {project.aspectRatio}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {/* 哨兵元素：滚动到此处时触发加载下一页 */}
          <div ref={sentinelRef} style={{ height: '1px' }} />
          {loadingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
              <div className="loading-spinner" style={{ width: '24px', height: '24px', borderWidth: '2px' }} />
            </div>
          )}
          {!hasMore && (
            <p style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem', padding: '1rem 0' }}>{t('loaded.all')}</p>
          )}
        </>
      ) : (
        <EmptyState
          icon={debouncedSearch ? '🔍' : activeTab === 'favorites' ? '★' : '🎬'}
          title={debouncedSearch ? t('search.noResults') : activeTab === 'favorites' ? t('empty.favoritesTitle') : t('empty.allTitle')}
          description={debouncedSearch
            ? t('search.noResultsDesc', { keyword: debouncedSearch })
            : activeTab === 'favorites'
            ? t('empty.favoritesDesc')
            : t('empty.allDesc')}
          action={activeTab === 'favorites' ? (
            <button className="btn btn-primary" onClick={() => handleTabChange('all')}>
              {t('empty.viewAll')}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleOpenCreate}>
              + {t('createProject')}
            </button>
          )}
        />
      )}

      {/* Clone Step 0: 资产复制选项 */}
      <CloneOptionsModal
        open={cloneStep0Open}
        sourceName={cloneSourceName}
        sourceWorkflow={cloneSourceWorkflow}
        onClose={() => {
          setCloneStep0Open(false);
          setCloneSourceId(null);
          setCloneSourceInherited(null);
          setCloneSourceWorkflow(undefined);
        }}
        onConfirm={(opts) => {
          setCloneOptions(opts);
          if (cloneSourceInherited) {
            setForm(prev => ({
              ...prev,
              // 仅用于克隆向导里的只读展示；克隆提交不发 analysisMode，后端恒继承源
              analysisMode: cloneSourceInherited.analysisMode,
              ...(opts.keepShotPrompts ? {
                skillId: cloneSourceInherited.skillId,
                scriptSkillId: cloneSourceInherited.scriptSkillId,
                languageSkillId: cloneSourceInherited.languageSkillId,
                outputLanguage: cloneSourceInherited.outputLanguage,
              } : {}),
            }));
          }
          setCloneStep0Open(false);
          setTypeSelectOpen(true); // 跳转 Step 1（类型选择）
        }}
      />

      {/* Step 1: 选择项目类型 */}
      <Modal open={typeSelectOpen && !modalOpen} onClose={handleCloseModal} title={t('typeSelect.title')} width="800px" solidBackdrop>
        <div>
          <p style={{ color: 'var(--warning)', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            {t('typeSelect.subtitle')}
          </p>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
            {([
              { value: 'animation', label: t('typeSelect.animation'), img: '/comic.png', badge: null, bandColor: 'rgba(0,210,80,0.75)' },
              { value: 'real_person', label: t('typeSelect.realPerson'), img: '/realastic.png', badge: t('typeSelect.realPersonBadge'), bandColor: 'rgba(0,140,255,0.75)' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setForm({ ...form, projectType: opt.value });
                  setModalOpen(true);
                }}
                style={{
                  width: isMobile ? '100%' : '400px', maxWidth: '400px', height: isMobile ? '200px' : '300px', padding: 0, border: 'none',
                  borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                  backgroundImage: `url(${opt.img})`, backgroundSize: 'cover', backgroundPosition: 'center',
                  transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column',
                  justifyContent: 'flex-end', position: 'relative', overflow: 'hidden',
                  boxShadow: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `inset 0 0 0 4px ${opt.bandColor}`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {opt.badge && (
                  <span style={{
                    position: 'absolute', top: '10px', right: '10px',
                    fontSize: '0.85rem', color: 'white', background: '#e91e78',
                    padding: '3px 10px', borderRadius: '4px', fontWeight: 600,
                  }}>{opt.badge}</span>
                )}
                <span style={{
                  width: '100%', padding: '12px 0',
                  background: opt.bandColor,
                  fontSize: '2rem', fontWeight: 700, color: 'white', textAlign: 'center',
                }}>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* Step 2: 填写项目信息 */}
      <Modal open={modalOpen} onClose={handleCloseModal} title={t('form.title')} width={isMobile ? '563px' : '1080px'} solidBackdrop
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={creating}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              form="create-project-form"
              className="btn btn-primary"
              disabled={!form.name.trim() || creating}
            >
              {creating ? (
                <>
                  <span className="loading-spinner"></span>
                  {t('form.creating')}
                </>
              ) : (
                t('form.create')
              )}
            </button>
          </>
        }
      >
        <form id="create-project-form" onSubmit={handleCreateProject}>
          {/* 两列布局：左列=无展开项的固定字段；右列=选中/勾选后会拉出隐藏选项的字段。
              移动端退回单列（block 自然堆叠），避免窄屏挤压。 */}
          <div style={isMobile ? undefined : { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '2rem', alignItems: 'start' }}>
          <div>
          <div className="form-group">
            <label className="form-label">{t('form.projectName')}</label>
            <input
              type="text"
              className="input"
              placeholder={t('form.projectNamePlaceholder')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={creating}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('form.projectType')}</label>
            <div style={{
              padding: '0.5rem 0.75rem', background: 'var(--bg-2)', borderRadius: '8px',
              border: '1px solid var(--border)', fontSize: '0.9rem', color: 'var(--text-1)',
            }}>
              {form.projectType === 'real_person' ? t('form.realPersonDisplay') : t('form.animationDisplay')}
              <span style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginLeft: '0.5rem' }}>{t('form.typeReadonly')}</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('form.projectDesc')}</label>
            <textarea
              className="input textarea"
              rows={1}
              style={{ minHeight: 'unset', resize: 'vertical' }}
              placeholder={t('form.projectDescPlaceholder')}
              value={form.projectPrompt}
              onChange={(e) => setForm({ ...form, projectPrompt: e.target.value })}
              disabled={creating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('form.aspectRatio')}<HelpTip text={t('form.aspectRatioTip')} /></label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {([
                { ratio: '9:16' as const, icon: (
                  <svg width="18" height="28" viewBox="0 0 18 28" fill="none" style={{ display: 'block' }}>
                    <rect x="1" y="1" width="16" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    <line x1="6" y1="25" x2="12" y2="25" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                ) },
                { ratio: '16:9' as const, icon: (
                  <svg width="30" height="20" viewBox="0 0 30 20" fill="none" style={{ display: 'block' }}>
                    <rect x="1" y="1" width="28" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    <line x1="13" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                ) },
                { ratio: '21:9' as const, icon: (
                  <svg width="38" height="18" viewBox="0 0 38 18" fill="none" style={{ display: 'block' }}>
                    <rect x="1" y="1" width="36" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    <line x1="17" y1="14" x2="21" y2="14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                ) },
              ]).map(({ ratio, icon }) => (
                <button
                  key={ratio}
                  type="button"
                  onClick={() => setForm({ ...form, aspectRatio: ratio })}
                  disabled={creating}
                  style={{
                    flex: 1, padding: '0.5rem 0.4rem', border: '1px solid',
                    borderRadius: '8px', cursor: creating ? 'not-allowed' : 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                    gap: '0.3rem', minHeight: '56px',
                    transition: 'all 0.15s',
                    background: form.aspectRatio === ratio ? 'var(--primary)' : 'transparent',
                    color: form.aspectRatio === ratio ? 'white' : 'var(--text-2)',
                    borderColor: form.aspectRatio === ratio ? 'var(--primary)' : 'var(--border)',
                  }}
                >
                  <span style={{ flex: 1, display: 'flex', alignItems: 'center' }}>{icon}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{ratio}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: creating ? 'not-allowed' : 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={form.autoStartAnalysis}
                onChange={(e) => setForm({ ...form, autoStartAnalysis: e.target.checked })}
                disabled={creating}
              />
              {t('form.autoStartAnalysis')}
              <HelpTip text={t('form.autoStartAnalysisTip')} />
            </label>
          </div>
          {/* 禁止视频生成BGM */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: creating ? 'not-allowed' : 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={form.disableBGM}
                onChange={(e) => setForm({ ...form, disableBGM: e.target.checked })}
                disabled={creating}
              />
              {t('form.disableBGM')}
              <HelpTip text={t('form.disableBGMTip')} />
            </label>
          </div>
          </div>
          {/* 右列：带展开项的字段，桌面端用左边框做视觉分隔 */}
          <div style={isMobile ? undefined : { borderLeft: '1px solid var(--border)', paddingLeft: '2rem' }}>
          {skills.length > 0 && (
            <div className="form-group">
              <label className="form-label">{t('form.shotSkill')}<HelpTip text={t('form.shotSkillTip')} /></label>
              <select
                className="input"
                value={form.skillId}
                onChange={(e) => {
                  const newSkill = skills.find(s => s.id === e.target.value);
                  setShotSkillError(null);
                  setForm({ ...form, skillId: e.target.value, skillVariableValues: shotSkillVariableDefaults(newSkill) });
                }}
                disabled={creating || lockSkillsAndLanguage}
              >
                {skills.map(skill => {
                  const st = skill.status || 'published';
                  const suffix = skill.isDefault ? t('form.skillDefault') : st === 'draft' ? t('form.skillDraft') : st === 'pending_review' ? t('form.skillPendingReview') : st === 'unpublished' ? t('form.skillUnpublished') : '';
                  return (
                  <option key={skill.id} value={skill.id} title={skill.description || ''}>
                    {skill.name}{suffix}
                  </option>
                  );
                })}
              </select>
              {shotSkillError && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--danger)' }}>{shotSkillError}</p>
              )}
              {lockSkillsAndLanguage && inheritedHint}
              {(() => {
                const sel = skills.find(s => s.id === form.skillId);
                return sel?.description ? (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>{sel.description}</p>
                ) : null;
              })()}
              {!lockSkillsAndLanguage && (() => {
                const selectedSkill = skills.find(s => s.id === form.skillId);
                return selectedSkill?.variables && selectedSkill.variables.length > 0 ? (
                  <SkillVariableForm
                    variables={selectedSkill.variables}
                    values={form.skillVariableValues}
                    onChange={v => setForm({ ...form, skillVariableValues: v })}
                  />
                ) : null;
              })()}
            </div>
          )}
          {scriptSkills.length > 0 && (
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                {t('form.scriptSkill')}
                <HelpTip text={t('form.enableRewriteTip')} />
              </label>
              <select
                className="input"
                value={form.scriptSkillId}
                onChange={(e) => {
                  const newSkill = scriptSkills.find(s => s.id === e.target.value);
                  const defaults: Record<string, string> = {};
                  (newSkill?.variables || []).forEach(v => { if (v.default) defaults[v.key] = v.default; });
                  setScriptSkillError(null);
                  setForm({ ...form, scriptSkillId: e.target.value, scriptSkillValues: defaults });
                }}
                disabled={creating || lockSkillsAndLanguage}
              >
                <option value="">{t('form.scriptSkillPlaceholder')}</option>
                {scriptSkills.map(skill => {
                  const st = skill.status || 'published';
                  const suffix = skill.isDefault ? t('form.skillDefault') : st === 'draft' ? t('form.skillDraft') : st === 'pending_review' ? t('form.skillPendingReview') : st === 'unpublished' ? t('form.skillUnpublished') : '';
                  return (
                  <option key={skill.id} value={skill.id} title={skill.description || ''}>
                    {skill.name}{suffix}
                  </option>
                  );
                })}
              </select>
              {scriptSkillError && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--danger)' }}>{scriptSkillError}</p>
              )}
              {lockSkillsAndLanguage && inheritedHint}
              {(() => {
                const sel = scriptSkills.find(s => s.id === form.scriptSkillId);
                return sel?.description ? (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>{sel.description}</p>
                ) : null;
              })()}
              {!lockSkillsAndLanguage && (() => {
                const selectedSkill = scriptSkills.find(s => s.id === form.scriptSkillId);
                return selectedSkill?.variables && selectedSkill.variables.length > 0 ? (
                  <SkillVariableForm
                    variables={selectedSkill.variables}
                    values={form.scriptSkillValues}
                    onChange={v => setForm({ ...form, scriptSkillValues: v })}
                  />
                ) : null;
              })()}
            </div>
          )}
          {/* 语种改写技能（选定后自动决定输出语言） */}
          <div className="form-group">
            <label className="form-label">{t('form.langSkill')}<HelpTip text={t('form.langSkillTip')} /></label>
            <select
              className="input"
              value={form.languageSkillId}
              onChange={(e) => {
                const newSkill = langSkills.find(s => s.id === e.target.value);
                const defaults: Record<string, string> = {};
                (newSkill?.variables || []).forEach(v => { if (v.default) defaults[v.key] = v.default; });
                setForm({
                  ...form,
                  languageSkillId: e.target.value,
                  outputLanguage: newSkill?.languageCode || '',
                  languageSkillValues: defaults,
                });
              }}
              disabled={creating || lockSkillsAndLanguage}
              style={{ cursor: creating ? 'not-allowed' : 'pointer' }}
            >
              <option value="">{t('form.langSkillPlaceholder')}</option>
              {langSkills.map(skill => {
                const lang = languages.find(l => l.code === skill.languageCode);
                const langLabel = lang?.name || skill.languageCode;
                return (
                  <option key={skill.id} value={skill.id}>
                    {skill.name} · {langLabel}{skill.isDefault ? t('form.skillDefault') : ''}
                  </option>
                );
              })}
            </select>
            {lockSkillsAndLanguage && inheritedHint}
            {!lockSkillsAndLanguage && (() => {
              const selected = langSkills.find(s => s.id === form.languageSkillId);
              const vars = selected?.variables || [];
              if (vars.length === 0) return null;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                  {vars.map(v => (
                    <div key={v.key}>
                      <label style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.2rem' }}>
                        {v.label || v.key}
                      </label>
                      <input
                        className="input"
                        value={form.languageSkillValues[v.key] ?? ''}
                        onChange={(e) => setForm({ ...form, languageSkillValues: { ...form.languageSkillValues, [v.key]: e.target.value } })}
                        placeholder={v.default || ''}
                        style={{ fontSize: '0.85rem' }}
                        disabled={creating}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
            {!langSkillsLoading && langSkills.length === 0 && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-3)' }}>
                {t('form.noLangSkills')}
              </p>
            )}
          </div>
          <div className="form-group">
            {lockAnalysisMode ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-1)' }}>{t('form.analysisModeToggle')}</span>
                  <HelpTip text={t('form.analysisModeTip')} />
                </div>
                <div className="input" style={{ background: 'var(--bg)', color: 'var(--text-2)', cursor: 'not-allowed' }}>
                  {t(analysisModeTitleKey(cloneSourceInherited?.analysisMode ?? 'basic'))}
                </div>
                {inheritedHint}
              </>
            ) : (() => {
              // 默认全部折叠（连资深都不显示），但默认仍选中资深(external)。点箭头展开全部 3 个选项；
              // 选了高级/简易则持续可见；仍是资深时编辑其它字段会自动收起。external 不可用时直接展示。
              const optionsVisible = showAnalysisOptions || form.analysisMode === 'advanced' || form.analysisMode === 'basic' || (externalAvailabilityChecked && !externalAvailable);
              const options = [
                ...(externalAvailable ? [{ value: 'external' as const, title: t('form.analysisExternal'), desc: t('form.analysisExternalDesc') }] : []),
                { value: 'advanced' as const, title: t('form.analysisAdvanced'), desc: t('form.analysisAdvancedDesc') },
                { value: 'basic' as const, title: t('form.analysisBasic'), desc: t('form.analysisBasicDesc') },
              ];
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: optionsVisible ? '0.5rem' : 0 }}>
                    {/* 小标题左侧箭头：点击折叠/展开全部分析模式选项 */}
                    <button
                      type="button"
                      onClick={() => setShowAnalysisOptions(v => !v)}
                      disabled={creating}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: 'none', padding: 0, cursor: creating ? 'not-allowed' : 'pointer', color: 'var(--text-1)', fontSize: '0.875rem', fontWeight: 600 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: optionsVisible ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      {t('form.analysisModeToggle')}
                    </button>
                    <HelpTip text={t('form.analysisModeTip')} />
                    {/* 右侧灰色说明文字（仅说明，不可点击） */}
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', fontWeight: 400, lineHeight: 1.4 }}>
                      {t('form.analysisLegacyHint')}
                    </span>
                  </div>
                  {optionsVisible && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                      {options.map(({ value, title, desc }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setForm({ ...form, analysisMode: value })}
                          disabled={creating}
                          style={{
                            flex: 1, padding: '0.45rem 0.5rem', border: '1px solid',
                            borderRadius: '6px', cursor: creating ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                            transition: 'all 0.15s',
                            background: form.analysisMode === value ? 'var(--primary)' : 'transparent',
                            color: form.analysisMode === value ? 'white' : 'var(--text-2)',
                            borderColor: form.analysisMode === value ? 'var(--primary)' : 'var(--border)',
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, flexShrink: 0, width: '2em', lineHeight: 1.4, textAlign: 'center' }}>{title}</span>
                          <span style={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right', flex: 1, whiteSpace: 'pre-line', lineHeight: 1.4 }}>{desc}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* OS 解说音色设置 */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: creating ? 'not-allowed' : 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={form.enableVoiceInsertion}
                onChange={(e) => setForm({ ...form, enableVoiceInsertion: e.target.checked })}
                disabled={creating}
              />
              {t('form.enableVoice')}
              <HelpTip text={t('form.enableVoiceTip')} />
            </label>
          </div>
          {form.enableVoiceInsertion && (
            <>
              {voicePresets.length > 0 && (
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>{t('form.voicePreset')}</label>
                  <select
                    className="input"
                    value=""
                    onChange={(e) => {
                      const preset = voicePresets.find(p => p.id === e.target.value);
                      if (preset) {
                        setForm(f => ({ ...f, voicePrompt: preset.prompt }));
                      }
                    }}
                    disabled={creating}
                  >
                    <option value="">{t('form.voicePresetPlaceholder')}</option>
                    {voicePresets.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>{t('form.voiceDesc')}</label>
                <textarea
                  className="input textarea"
                  style={{ minHeight: '60px' }}
                  value={form.voicePrompt}
                  onChange={(e) => setForm({ ...form, voicePrompt: e.target.value })}
                  placeholder={t('form.voiceDescPlaceholder')}
                  disabled={creating}
                />
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
                  {t('form.voiceDescHint')}
                </p>
              </div>
            </>
          )}
          {/* 参考翻译入口：右下角黄色文字链，点开引导弹窗再选语言（选了才会自动翻译） */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setRefLangGuideOpen(true)}
              disabled={creating}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: creating ? 'not-allowed' : 'pointer',
                color: 'var(--warning)', fontSize: '0.82rem', textDecoration: 'underline', textUnderlineOffset: '3px',
              }}
            >
              {t('form.refLangHook')}
              {form.refLanguages.length > 0 && ` (${form.refLanguages.join(', ')})`}
            </button>
          </div>
          </div>
          </div>
        </form>
      </Modal>

      {/* 参考翻译引导弹窗：说明 + 语言勾选（仅辅助阅读，不改生成语言） */}
      <Modal
        open={refLangGuideOpen}
        onClose={() => setRefLangGuideOpen(false)}
        title={t('form.refLangGuideTitle')}
        width="480px"
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setRefLangGuideOpen(false)}>
            {t('form.refLangGuideDone')}
          </button>
        }
      >
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: 'var(--text-1)', lineHeight: 1.7 }}>
          {t('form.refLangGuideIntro')}
        </p>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--warning)', lineHeight: 1.7 }}>
          {t('form.refLangGuideNotGen')}
        </p>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--text-2)', lineHeight: 1.7 }}>
          {t('form.refLangGuideOnly')}
        </p>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">{t('form.refLangSelectLabel')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {refLangOptions.length === 0 && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('form.refLanguagesEmpty')}</span>
            )}
            {refLangOptions.map(lang => {
              const checked = form.refLanguages.includes(lang);
              return (
                <label key={lang} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: creating ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={creating}
                    onChange={() => setForm({
                      ...form,
                      refLanguages: checked
                        ? form.refLanguages.filter(l => l !== lang)
                        : [...form.refLanguages, lang],
                    })}
                  />
                  {lang}
                </label>
              );
            })}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={showRewriteConfirm}
        onCancel={() => setShowRewriteConfirm(false)}
        onConfirm={doCreateProject}
        title={t('rewriteConfirm.title')}
        message={(() => {
          const skill = scriptSkills.find(s => s.id === form.scriptSkillId);
          return (
            <>
              {t('rewriteConfirm.description')}
              <div style={{ margin: '0.75rem 0', padding: '0.75rem', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)' }}>
                <strong>{skill?.name}</strong>
                {skill?.description && <span style={{ color: 'var(--text-2)' }}>：{skill.description}</span>}
              </div>
              {t('rewriteConfirm.descriptionEnd')}
            </>
          );
        })()}
        confirmText={t('rewriteConfirm.confirm')}
        cancelText={t('cancel', { ns: 'common' })}
        loading={creating}
      />

      <Modal open={showPhoneWarning} onClose={() => setShowPhoneWarning(false)} title={t('phoneWarning.title')} width="400px">
        <p style={{ margin: '0 0 1rem', color: 'var(--text-2)' }}>
          {t('phoneWarning.message')}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={() => setShowPhoneWarning(false)}>{t('cancel', { ns: 'common' })}</button>
          <button className="btn btn-primary" onClick={() => { setShowPhoneWarning(false); navigate('/profile'); }}>
            {t('phoneWarning.goToSettings')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectList;
